import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Phone, ArrowRight, MapPin, Car, Clock, Train } from "lucide-react";

const PHONE_LINK = "tel:+33549057490";

export const PanneVilleHero = ({ combo, Icon }) => {
  const { panne, city, h1 } = combo;
  return (
    <section className="relative py-16 md:py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-red-900 text-white overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 right-10 w-72 h-72 bg-red-500 rounded-full blur-3xl"></div>
      </div>

      <div className="container-custom relative">
        <Badge className="bg-white/10 text-white border border-white/20 px-4 py-2 mb-6 hover:bg-white/10">
          <Icon className="w-4 h-4 mr-2" />
          {panne.category} • {city.name} → Niort 79000 • {city.distanceKm} km
        </Badge>

        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
          {h1}
        </h1>

        <p className="text-lg md:text-xl text-slate-200 max-w-3xl mb-8 leading-relaxed">
          Vous habitez à <strong className="text-white">{city.name} ({city.postalCode})</strong> et
          votre appareil est en panne ? Mediastore Niort est à seulement
          <strong className="text-red-400"> {city.distanceKm} km ({city.timeMinutes} min par {city.accessRoad})</strong>.
          Diagnostic gratuit, intervention en {panne.durationLabel}, garantie 6 mois. Dès
          <strong className="text-red-400"> {panne.priceFrom} €</strong>.
        </p>

        <div className="flex flex-col sm:flex-row gap-4">
          <a href={PHONE_LINK}>
            <Button size="lg" className="w-full sm:w-auto bg-red-600 hover:bg-red-700 h-14 px-8 gap-2 text-lg font-semibold shadow-xl" data-testid="pv-call-btn">
              <Phone className="h-5 w-5" />
              05 49 05 74 90
            </Button>
          </a>
          <Link to="/rendez-vous">
            <Button size="lg" variant="outline" className="w-full sm:w-auto h-14 px-8 gap-2 border-2 border-white/30 text-white hover:bg-white/10" data-testid="pv-rdv-btn">
              Prendre rendez-vous
              <ArrowRight className="h-5 w-5" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export const PanneVilleAccess = ({ city }) => (
  <section className="section-padding bg-white" data-testid="pv-access">
    <div className="container-custom max-w-5xl">
      <div className="text-center mb-10">
        <Badge className="bg-red-100 text-red-700 px-4 py-2 mb-4 hover:bg-red-100">
          <Car className="w-4 h-4 mr-1" />
          Trajet & accès
        </Badge>
        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
          Depuis {city.name} jusqu&apos;à Mediastore Niort
        </h2>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <Card className="border-2 border-red-100">
          <CardContent className="p-6 text-center">
            <Car className="h-10 w-10 text-red-600 mx-auto mb-3" />
            <p className="text-3xl font-bold text-slate-900 mb-1">{city.distanceKm} km</p>
            <p className="text-slate-600">par {city.accessRoad}</p>
          </CardContent>
        </Card>
        <Card className="border-2 border-red-100">
          <CardContent className="p-6 text-center">
            <Clock className="h-10 w-10 text-red-600 mx-auto mb-3" />
            <p className="text-3xl font-bold text-slate-900 mb-1">{city.timeMinutes} min</p>
            <p className="text-slate-600">de trajet en voiture</p>
          </CardContent>
        </Card>
        <Card className="border-2 border-red-100">
          <CardContent className="p-6 text-center">
            <Train className="h-10 w-10 text-red-600 mx-auto mb-3" />
            <p className="text-lg font-semibold text-slate-900 mb-1">Transports</p>
            <p className="text-sm text-slate-600">{city.publicTransport}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-gradient-to-br from-slate-50 to-white border-slate-200">
        <CardContent className="p-8">
          <h3 className="text-xl font-bold text-slate-900 mb-3 flex items-center gap-2">
            <MapPin className="h-5 w-5 text-red-600" />
            {city.name} en bref
          </h3>
          <div className="grid sm:grid-cols-2 gap-4 text-slate-700 mb-4">
            <div>
              <p className="text-sm text-slate-500 mb-1">Code postal</p>
              <p className="font-semibold">{city.postalCode}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">Intercommunalité</p>
              <p className="font-semibold">{city.intercommunalite}</p>
            </div>
          </div>
          <p className="text-slate-600 leading-relaxed text-sm">{city.clientStory}</p>
        </CardContent>
      </Card>
    </div>
  </section>
);
